import { useMemo } from "react";
import { Box, Paper, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { BarChart } from "@components/bar-chart";
import { StackedBarChart } from "@components/stacked-bar-chart";
import { PieChart } from "@components/pie-chart";
import { MultiLineChart } from "@components/multi-line-chart";
import type { MultiLineChartSeries } from "@components/multi-line-chart";
import { HeatmapChart } from "@components/heatmap-chart";
import { ExportTable } from "@components/export-table";
import { defaultPieChartColorRange } from "@components/pie-chart/utils";
import type { ChartConfig } from "@hooks/use-reports/types";
import type { DynamicFilterState } from "@hooks/use-report-filter";
import type { AggregateDataItem } from "@hooks/use-reports/types";
import { getFieldLabel, enrichChartWithLabelMaps } from "@hooks/use-reports/shared/column-builder";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import { underscoreToTitleCase } from "@utils/index";
import {
  isBooleanLike,
  isBooleanField,
  transformLabel as transformLabelUtil,
} from "@utils/field-detection-utils";
import type { ExportColumnOption } from "@components/export-table";
import type { AssetTableColumnFromApi } from "@hooks/use-config-context/asset/types";
import type { TicketTableColumnFromApi } from "@hooks/use-config-context/ticket/types";
import type { UserTableColumnFromApi } from "@hooks/use-config-context/user/types";

export interface ChartsGridSectionProps {
  charts: readonly ChartConfig[];
  chartData: Record<string, any>;
  textColor: string;
  lineColor: string;
  filters: DynamicFilterState;
  dataSource?: "Assets" | "Tickets" | "Users";
  onDetailExport?: (selectedRows: any[], columns?: ExportColumnOption[]) => void;
  detailExportLoading?: boolean;
}

/**
 * Transform chart label values using utility, with chart-specific labelMap support
 * Falls back to underscoreToTitleCase for unmapped values
 */
const getTransformedLabel = (rawValue: any, chart: ChartConfig, fieldIsBooleanDetected: boolean = false): string => {
  // Try utility transformation first (handles boolean detection and labelMap)
  const transformed = transformLabelUtil(rawValue, chart.labelMap, fieldIsBooleanDetected);
  if (transformed !== undefined) {
    return transformed;
  }
  // Fallback to underscore-to-titlecase for unmapped string values
  return underscoreToTitleCase(String(rawValue));
};

export const ChartsGridSection = ({
  charts,
  chartData,
  textColor,
  lineColor,
  filters,
  dataSource = "Assets",
  onDetailExport,
  detailExportLoading = false,
}: ChartsGridSectionProps) => {
  const assetsConfig = useAssetsConfigContext();
  const ticketConfig = useTicketConfigContext();
  const userConfig = useUserConfigContext();

  const wpConfig = dataSource === "Assets"
    ? assetsConfig.config
    : dataSource === "Tickets"
    ? ticketConfig.config
    : userConfig.config;

  const exportColumnOptions: ExportColumnOption[] = useMemo(() => {
    const cols = dataSource === "Assets"
      ? (assetsConfig.tableColumns as AssetTableColumnFromApi[])
      : dataSource === "Tickets"
      ? (ticketConfig.tableColumns as TicketTableColumnFromApi[])
      : (userConfig.tableColumns as UserTableColumnFromApi[]);
    const userExportableFieldIds = new Set(
      Object.values(userConfig.config || {})
        .filter((field: any) => field.export)
        .map((field: any) => field.id)
    );
    return cols
      .filter((col) => {
        if (col.id === "id") {
          return false;
        }

        if (dataSource === "Users") {
          return userExportableFieldIds.has(col.id);
        }

        return true;
      })
      .map((col) => ({
        key: dataSource === "Users"
          ? col.id
          : col.id === "title"
          ? "title.rendered"
          : `meta_box.${col.id}`,
        label: col.label,
      }));
  }, [assetsConfig.tableColumns, dataSource, ticketConfig.tableColumns, userConfig.config, userConfig.tableColumns]);

  // Enrich charts with labelMaps from wpConfig
  const enrichedCharts = useMemo(() => {
    return charts.map((chart) => {
      const dimensions = (chart as any).dimensions || [];
      return enrichChartWithLabelMaps(chart, dimensions, wpConfig);
    });
  }, [charts, wpConfig]);

  const renderChart = (chart: ChartConfig) => {
    const data = chartData[chart.id as keyof typeof chartData];

    if (!data || (Array.isArray(data) && data.length === 0)) {
      return (
        <Typography variant="body2" color="textSecondary">
          No data available
        </Typography>
      );
    }

    const toSingleLineSeries = (lineData: any[]): MultiLineChartSeries[] => {
      // If already in MultiLineChartSeries format, keep the first series for singular line charts.
      if (lineData[0]?.data && Array.isArray(lineData[0].data)) {
        const firstSeries = lineData[0] as MultiLineChartSeries;
        return [{
          id: chart.id,
          data: firstSeries.data,
        }];
      }

      return [
        {
          id: chart.id,
          data: lineData.map((item) => ({
            x: getTransformedLabel(item.label || item.id, chart),
            y: item.count ?? 0,
          })),
        },
      ];
    };

    switch (chart.type) {
      case "line": {
        const lineData = data as any[];
        if (lineData.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              No data available
            </Typography>
          );
        }

        return (
          <Box className="chart-box">
            <MultiLineChart
              data={toSingleLineSeries(lineData)}
              textColor={textColor}
              lineColor={lineColor}
            />
          </Box>
        );
      }

      case "multi-line":
        // Multi-line chart expects MultiLineChartSeries[] format
        // For a single dimension, convert aggregated data to line format
        const lineData = data as any[];
        if (lineData.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              No data available
            </Typography>
          );
        }
        
        // Check if data is already in MultiLineChartSeries format
        if (lineData[0]?.data && Array.isArray(lineData[0].data)) {
          return (
            <Box className="chart-box">
              <MultiLineChart
                data={lineData as MultiLineChartSeries[]}
                textColor={textColor}
                lineColor={lineColor}
              />
            </Box>
          );
        }
        
        // Convert aggregated data to line series
        const lineSeries: MultiLineChartSeries[] = [
          {
            id: chart.id,
            data: lineData.map((item) => ({
              x: getTransformedLabel(item.label || item.id, chart),
              y: item.count ?? 0,
            })),
          },
        ];
        
        return (
          <Box className="chart-box">
            <MultiLineChart
              data={lineSeries}
              textColor={textColor}
              lineColor={lineColor}
            />
          </Box>
        );

      case "bar":
        // Check if the dimension field is boolean to enable auto-transformation
        const barDimensionField = chart.dimensions?.[0];
        const isBarBooleanField = barDimensionField ? isBooleanField(data as any[], barDimensionField) : false;
        
        return (
          <Box className="chart-box">
            <BarChart
              data={data as AggregateDataItem[]}
              textColor={textColor}
              getValue={(d) => d.count}
              getLabel={(d) => getTransformedLabel(d.label, chart, isBarBooleanField)}
              lineColor={lineColor}
              getId={(d) => d.value || "unknown"}
            />
          </Box>
        );

      case "pie":
        const pieData = data as AggregateDataItem[];
        const pieDimensionField = chart.dimensions?.[0];
        const isPieBooleanField = pieDimensionField ? isBooleanField(pieData, pieDimensionField) : false;
        
        return (
          <Box className="chart-box">
            <PieChart
              data={pieData}
              textColor={textColor}
              getValue={(d) => d.count}
              getLabel={(d) => {
                const transformedLabel = getTransformedLabel(d.label, chart, isPieBooleanField);
                return `${transformedLabel} (${(d.count as any).toLocaleString?.() || d.count})`;
              }}
              strokeColor={lineColor}
              getId={(d) => d.value || "unknown"}
            />
          </Box>
        );

      case "stacked-bar":
        // For stacked bar, the first row determines structure
        const stackedData = data as Record<string, any>[];
        if (stackedData.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              No data available
            </Typography>
          );
        }

        // Find the row identifier key (usually the primary dimension, e.g., "asset_site")
        // It's the key with numeric or string values that are unique per row (not numeric aggregates)
        const firstRow = stackedData[0];
        const allKeys = Object.keys(firstRow);
        
        // The primary row key is typically at position 0 or labeled as a dimension
        // We heuristically find it by checking for non-numeric values or using the leftmost key
        const xKey = allKeys[0]; // Use the first key as x-axis (primary dimension)
        const seriesKeys = allKeys.filter((k) => k !== xKey); // All other keys are data series

        // Check if xKey field is boolean to enable auto-transformation
        const isStackedBarBooleanField = isBooleanField(stackedData, xKey);

        // Transform xKey values using labelMap or auto-boolean detection
        const transformedStackedData = stackedData.map((row) => ({
          ...row,
          [xKey]: getTransformedLabel(String(row[xKey]), chart, isStackedBarBooleanField),
        }));

        // Build keyLabels mapping for series
        const keyLabels: Record<string, string> = {};
        seriesKeys.forEach((seriesKey) => {
          keyLabels[seriesKey] = getTransformedLabel(seriesKey, chart, false);
        });

        return (
          <Box className="chart-box">
            <StackedBarChart
              data={transformedStackedData}
              xKey={xKey}
              keys={seriesKeys}
              keyLabels={keyLabels}
            />
          </Box>
        );

      case "heatmap":
        // Heatmap data should have row, col, value structure
        // Extract dynamic property names from config dimensions
        const heatmapData = (data as any[]);
        
        // Extract unique row and column values from the data, filtering out empty values
        const uniqueRowValues = Array.from(
          new Set(
            heatmapData
              .map((d) => d?.site || d?.[Object.keys(d || {})[0]])
              .filter((v): v is string => Boolean(v))
          )
        );
        const uniqueColValues = Array.from(
          new Set(
            heatmapData
              .map((d) => d?.status || d?.[Object.keys(d || {})[1]])
              .filter((v): v is string => Boolean(v))
          )
        );
        
        // Check if status column is boolean to enable auto-transformation
        const isHeatmapStatusBooleanField = uniqueColValues.every((val) => isBooleanLike(val));
        
        if (uniqueRowValues.length === 0 || uniqueColValues.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              No data available for heatmap
            </Typography>
          );
        }
        
        return (
          <Box className="chart-box">
            <HeatmapChart
              data={heatmapData}
              sites={uniqueRowValues}
              statuses={uniqueColValues}
              colorRange={["#f1f1f1", defaultPieChartColorRange[5]]}
              statusLabels={uniqueColValues.reduce(
                (acc, s) => ({ ...acc, [s]: getTransformedLabel(s, chart, isHeatmapStatusBooleanField) }),
                {}
              )}
            />
          </Box>
        );

      case "export-table": {
        // Export table renders the raw data as a table with columns from dimensions.
        // Raw field values are preserved in the data so the detail export can use them
        // as WP REST API filter params. Display transformation is applied via column.render.
        const tableData = (data as Record<string, any>[]) || [];
        const exportColumns = (chart.dimensions as string[]).map((fieldName) => ({
          key: fieldName,
          label: getFieldLabel(fieldName, wpConfig),
        }));

        // Keep raw values; only inject a stable row id
        const tableDataWithIds = tableData.map((row, idx) => ({
          ...row,
          id: `${chart.id}-${idx}`,
        }));

        return (
          <Box sx={{ height: 400, overflow: "auto" }}>
            <ExportTable
              title={chart.title || chart.id}
              exportTitle={chart.title || chart.id}
              titleTooltip={chart.tooltip}
              columns={exportColumns.map((col) => {
                  const fieldConfig = wpConfig?.[col.key];
                  const isBoolean = fieldConfig?.type === "radio";
                  return {
                    key: col.key as any,
                    label: col.label,
                    render: (value: any) => {
                      if (isBoolean) {
                        const isTrue = value === "1" || value === 1 || value === true || value === "true";
                        return isTrue
                          ? <CheckIcon sx={{ color: (theme) => theme.palette.success.main }} />
                          : <CloseIcon sx={{ color: (theme) => theme.palette.error.main }} />;
                      }
                      return getTransformedLabel(value, chart, false);
                    },
                  };
                })}
              data={tableDataWithIds}
              hoverable={true}
              stickyHeader={true}
              exportColumnOptions={exportColumnOptions}
              onDetailExport={onDetailExport}
              detailExportLoading={detailExportLoading}
            />
          </Box>
        );
      }

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            Unsupported chart type: {chart.type}
          </Typography>
        );
    }
  };

  return (
    <Box className="charts-grid">
      {enrichedCharts.map((chart) => (
        <Paper
          key={chart.id}
          className={`chart-container chart-container-${chart.width || "full"}width`}
          sx={{ minWidth: 0, overflow: "hidden" }}
        >
          {chart.type !== "export-table" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {chart.title || chart.id}
              </Typography>
              {chart.tooltip && (
                <Tooltip title={chart.tooltip} arrow placement="top">
                  <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: "help" }} />
                </Tooltip>
              )}
            </Box>
          )}
          {renderChart(chart)}
        </Paper>
      ))}
    </Box>
  );
};
